import React, { useEffect, useRef, useState } from "react";
import { ChatDisclaimer } from "@/components/AiNotice";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Send, Loader2, Trash2, Wine, Sparkles, Laugh } from "lucide-react";
import { KORRIGAN } from "@/constants/testIds";
import { chatKorrigan, chatKorriganHistory, chatKorriganClear, chatKorriganUsage } from "@/lib/api";
import { useLang } from "@/lib/lang";
import { useAccount } from "@/lib/account";
import { PLAYLISTS, ACCENTS } from "@/lib/tracks";
import MusicPicker from "@/components/MusicPicker";
import VoiceButton from "@/components/VoiceButton";

const SESSION_KEY = "astrovoyance.korrigan_session";

const SUGGESTIONS_FR = [
  "Toise-moi comme un aristocrate déchu",
  "Qu'as-tu à dire des coachs de vie ?",
  "Une pique grivoise à la Brassens",
  "Comment tu regardes les influenceurs ?",
  "Une satire des voyants qui font payer",
];
const SUGGESTIONS_EN = [
  "Look down on me like a fallen aristocrat",
  "What do you think of life coaches?",
  "A bawdy quip à la Brassens",
  "How do you see influencers?",
  "A satire of clairvoyants who charge",
];

export default function Korrigan() {
  const { lang, setLang } = useLang();
  const { email, status } = useAccount() || {};
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || "");
  const [usage, setUsage] = useState({ used_today: 0, limit: null, premium_active: true, remaining: null });
  const boxRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    chatKorriganHistory(sessionId).then((d) => setMessages(d.messages || [])).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    chatKorriganUsage(email).then(setUsage).catch(() => {});
  }, [email, status?.premium_active, messages.length]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed) return;
    setMessages((m) => [...m, { sender: "user", message: trimmed, created_at: new Date().toISOString() }]);
    setInput("");
    setLoading(true);
    try {
      const r = await chatKorrigan(trimmed, sessionId, email, lang);
      if (r.session_id && r.session_id !== sessionId) {
        setSessionId(r.session_id);
        localStorage.setItem(SESSION_KEY, r.session_id);
      }
      setMessages((m) => [...m, { sender: "advisor", message: r.reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail?.code === "daily_korrigan_limit_reached") {
        toast.error(lang === "fr"
          ? `Le Korrigan te chasse (${detail.limit}/jour). Passe Premium pour un mépris illimité.`
          : `The Korrigan dismisses you (${detail.limit}/day). Go Premium for unlimited contempt.`);
        setUsage((u) => ({ ...u, used_today: detail.used_today, remaining: 0 }));
      } else {
        toast.error(lang === "fr" ? "Le Korrigan feint l'indifférence. Réessaie, brave humain." : "The Korrigan feigns indifference. Try again, brave human.");
      }
    } finally {
      setLoading(false);
    }
  };

  const wipe = async () => {
    try { await chatKorriganClear(sessionId); } catch { /* ignore */ }
    localStorage.removeItem(SESSION_KEY);
    setSessionId("");
    setMessages([]);
    toast.success(lang === "fr" ? "Le Korrigan bâille — session effacée." : "The Korrigan yawns — session wiped.");
  };

  const suggestions = lang === "fr" ? SUGGESTIONS_FR : SUGGESTIONS_EN;

  return (
    <div data-testid={KORRIGAN.page} className="kor-root min-h-screen relative">
      <div className="kor-bg" aria-hidden="true" />
      <header className="relative z-30">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]" style={{ color: "#e07a4f" }}>
            <ArrowLeft size={14} /> {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-full text-[11px] font-mono overflow-hidden"
                 style={{ background: "rgba(90,45,25,0.35)", border: "1px solid rgba(224,122,79,0.28)" }}>
              <button onClick={() => setLang("fr")} type="button" className="px-2.5 py-1.5"
                      style={{ background: lang === "fr" ? "#e07a4f" : "transparent", color: lang === "fr" ? "#150804" : "#f0c8a5", fontWeight: lang === "fr" ? 600 : 400 }}>FR</button>
              <button onClick={() => setLang("en")} type="button" className="px-2.5 py-1.5"
                      style={{ background: lang === "en" ? "#e07a4f" : "transparent", color: lang === "en" ? "#150804" : "#f0c8a5", fontWeight: lang === "en" ? 600 : 400 }}>EN</button>
            </div>
            <MusicPicker playlist={PLAYLISTS.korrigan} accent={ACCENTS.korrigan} testId="korrigan-music" persona="Korrigan" />
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pt-4 pb-8">
        <div className="overline mb-4" style={{ color: "#e07a4f" }}>
          <span className="kicker-line" />
          {lang === "fr" ? "Le Korrigan · satire · grivoiserie · arrogance délicieuse" : "The Korrigan · satire · bawdy wit · delicious arrogance"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ color: "#f4ead0" }}>
          {lang === "fr" ? (
            <>Le <span className="italic" style={{ color: "#e07a4f" }}>Korrigan</span>, esprit toisant.</>
          ) : (
            <>The <span className="italic" style={{ color: "#e07a4f" }}>Korrigan</span>, sneering spirit.</>
          )}
        </h1>
        <p className="mt-5 max-w-2xl text-base sm:text-lg" style={{ color: "#f0c8a5" }}>
          {lang === "fr"
            ? "Le Korrigan te toise avec panache. Satire des puissants, grivoiserie à la Brassens, arrogance d'aristocrate déchu qui a lu Voltaire et fréquenté les tavernes. Il vise haut, jamais bas. Son mépris est une caresse."
            : "The Korrigan looks down on you with panache. Satire of the powerful, Brassens-style bawdy wit, arrogance of a fallen aristocrat who read Voltaire and haunted taverns. Aims high, never low. His contempt is a caress."}
        </p>

        {/* Usage counter — freemium 20/day */}
        <div data-testid="korrigan-usage"
             className="mt-6 inline-flex items-center gap-3 text-xs font-mono px-4 py-2 rounded-full"
             style={{ background: "rgba(30,15,6,0.7)",
                      border: "1px solid rgba(224,122,79,0.4)",
                      color: "#e07a4f" }}>
          <Laugh size={13} />
          {usage.premium_active
            ? (lang === "fr" ? "Illimité · Toujours gratuit" : "Unlimited · Always free")
            : (
                <>
                  {usage.used_today ?? 0} / {usage.limit ?? 20}{" "}
                  {lang === "fr" ? "piques aujourd'hui" : "quips today"}
                </>
              )}
        </div>
      </section>

      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pb-24">
        <div className="kor-chat" data-testid={KORRIGAN.chatBox}>
          <div ref={boxRef} className="kor-messages">
            {messages.length === 0 && (
              <div className="kor-empty">
                <Wine size={22} style={{ color: "#e07a4f" }} />
                <p className="mt-3 font-serif text-xl italic" style={{ color: "#f4ead0" }}>
                  {lang === "fr"
                    ? "\"Tiens, encore un humain. Qu'attends-tu de moi, pauvre créature ?\""
                    : "\"Well, another human. What do you want, poor creature?\""}
                </p>
                <p className="mt-4 text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "#c9a878" }}>
                  {lang === "fr" ? "Ou provoque-le :" : "Or provoke him:"}
                </p>
                <div className="mt-4 flex flex-wrap gap-2 justify-center">
                  {suggestions.map((s, i) => (
                    <button key={i} type="button"
                            data-testid={KORRIGAN.suggestion(i)}
                            onClick={() => send(s)}
                            className="kor-chip">
                      <Sparkles size={11} /> {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`kor-msg ${m.sender === "user" ? "user" : "advisor"}`}>
                <div className="kor-msg-inner">
                  {m.sender === "advisor" ? <ReactMarkdown>{m.message}</ReactMarkdown> : <span>{m.message}</span>}
                  {m.sender === "advisor" && (
                    <div className="mt-2">
                      <VoiceButton text={m.message} lang={lang} accent="#e07a4f" testId={`korrigan-voice-${i}`} />
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="kor-msg advisor">
                <div className="kor-msg-inner inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  <span className="text-sm" style={{ color: "#f0c8a5" }}>{lang === "fr" ? "Le Korrigan aiguise sa pique…" : "The Korrigan sharpens his quip…"}</span>
                </div>
              </div>
            )}
          </div>

          <form className="kor-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <textarea data-testid={KORRIGAN.input}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder={lang === "fr" ? "Provoque le Korrigan — il adore ça…" : "Provoke the Korrigan — he loves it…"}
                      rows={2}
                      className="kor-textarea"
                      disabled={loading} />
            <div className="flex items-center gap-2">
              <button type="button" onClick={wipe} data-testid={KORRIGAN.clear} className="kor-btn-ghost">
                <Trash2 size={14} />
              </button>
              <button type="submit" disabled={loading || !input.trim()} data-testid={KORRIGAN.send} className="kor-btn-primary">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {lang === "fr" ? "Envoyer" : "Send"}
              </button>
            </div>
          </form>
          <ChatDisclaimer testId="korrigan-ai-disclaimer" />
        </div>
      </section>

      <style>{`
        .kor-root { background: radial-gradient(ellipse at top, #1e0f04 0%, #0d0602 55%, #050201 100%); color: #f4ead0; overflow-x: hidden; }
        .kor-bg { position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background:
            radial-gradient(circle at 20% 25%, rgba(224,122,79,0.16) 0%, transparent 45%),
            radial-gradient(circle at 82% 78%, rgba(155,68,32,0.14) 0%, transparent 42%),
            linear-gradient(180deg, #1e0f04 0%, #05030a 100%); }
        .kor-chat { background: linear-gradient(180deg, rgba(28,14,6,0.9), rgba(10,4,2,0.94)); border: 1px solid rgba(224,122,79,0.28); border-radius: 22px; padding: 20px; box-shadow: 0 24px 60px -30px rgba(0,0,0,0.75); }
        .kor-messages { min-height: 380px; max-height: 62vh; overflow-y: auto; padding: 12px 4px 20px; margin-bottom: 16px; }
        .kor-empty { text-align: center; padding: 32px 20px; }
        .kor-chip { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 999px; background: rgba(90,45,25,0.5); border: 1px solid rgba(224,122,79,0.35); color: #f0c8a5; font-size: 12px; font-family: "IBM Plex Mono", monospace; transition: all 200ms ease; }
        .kor-chip:hover { background: rgba(224,122,79,0.22); border-color: rgba(224,122,79,0.75); color: #f4ead0; }
        .kor-msg { display: flex; margin: 10px 0; }
        .kor-msg.user { justify-content: flex-end; }
        .kor-msg.advisor { justify-content: flex-start; }
        .kor-msg-inner { max-width: 82%; padding: 14px 18px; border-radius: 16px; line-height: 1.6; font-size: 14.5px; }
        .kor-msg.user .kor-msg-inner { background: linear-gradient(135deg, #a45c34, #6d3a1e); color: #f8ecd0; border-bottom-right-radius: 4px; }
        .kor-msg.advisor .kor-msg-inner { background: rgba(30,16,8,0.9); color: #f4ead0; border: 1px solid rgba(224,122,79,0.22); border-bottom-left-radius: 4px; }
        .kor-msg-inner p { margin: 0 0 10px; }
        .kor-msg-inner p:last-child { margin-bottom: 0; }
        .kor-msg-inner strong { color: #e07a4f; }
        .kor-msg-inner em { color: #f0c8a5; }
        .kor-form { display: flex; gap: 10px; align-items: flex-end; border-top: 1px solid rgba(224,122,79,0.22); padding-top: 14px; }
        .kor-textarea { flex: 1; resize: none; padding: 12px 14px; border-radius: 14px; background: rgba(12,6,2,0.7); border: 1px solid rgba(224,122,79,0.28); color: #f4ead0; font-family: inherit; font-size: 14.5px; outline: none; }
        .kor-textarea:focus { border-color: rgba(224,122,79,0.75); }
        .kor-btn-primary { display: inline-flex; align-items: center; gap: 8px; padding: 12px 18px; border-radius: 12px; background: linear-gradient(135deg, #e07a4f, #a04520); color: #150804; font-weight: 600; font-size: 13px; font-family: "IBM Plex Mono", monospace; transition: transform 200ms ease, opacity 200ms ease; }
        .kor-btn-primary:hover:not(:disabled) { transform: translateY(-1px); }
        .kor-btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
        .kor-btn-ghost { display: inline-flex; align-items: center; padding: 12px 14px; border-radius: 12px; background: rgba(180,40,40,0.15); border: 1px solid rgba(255,120,120,0.3); color: #ffb8a8; }
      `}</style>
    </div>
  );
}
